import React, { useState, forwardRef } from 'react';
import classNames from 'classnames';

export interface AvatarProps extends React.HTMLAttributes<HTMLSpanElement> {
  src?: string;
  alt?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | number;
  shape?: 'circle' | 'square';
  icon?: React.ReactNode;
  name?: string;
}

const getInitials = (name: string): string => {
  const names = name.trim().split(' ');
  if (names.length === 1) {
    return names[0].charAt(0).toUpperCase();
  }
  return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase();
};

const Avatar = forwardRef<HTMLSpanElement, AvatarProps>(
  ({ className, src, alt, size = 'md', shape = 'circle', icon, name, children, style, ...rest }, ref) => {
    const [imgError, setImgError] = useState(false);

    const sizeClasses: Record<string, string> = {
      xs: 'ecme-avatar-xs',
      sm: 'ecme-avatar-sm',
      md: 'ecme-avatar-md',
      lg: 'ecme-avatar-lg',
      xl: 'ecme-avatar-xl',
    };

    const customSize = typeof size === 'number' ? { width: size, height: size, fontSize: size * 0.4 } : {};

    const avatarClass = classNames(
      'ecme-avatar',
      typeof size === 'string' && sizeClasses[size],
      {
        'ecme-avatar-circle': shape === 'circle',
        'ecme-avatar-square': shape === 'square',
      },
      className
    );

    const handleImgError = () => {
      setImgError(true);
    };

    const renderContent = () => {
      if (src && !imgError) {
        return <img src={src} alt={alt || name || ''} onError={handleImgError} className="ecme-avatar-img" />;
      }
      if (icon) {
        return <span className="ecme-avatar-icon">{icon}</span>;
      }
      if (name) {
        return <span className="ecme-avatar-text">{getInitials(name)}</span>;
      }
      if (children) {
        return <span className="ecme-avatar-text">{children}</span>;
      }
      return (
        <svg className="ecme-avatar-icon" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
        </svg>
      );
    };

    return (
      <span ref={ref} className={avatarClass} style={{ ...customSize, ...style }} {...rest}>
        {renderContent()}
      </span>
    );
  }
);

Avatar.displayName = 'Avatar';

export interface AvatarGroupProps extends React.HTMLAttributes<HTMLDivElement> {
  maxCount?: number;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | number;
}

export const AvatarGroup: React.FC<AvatarGroupProps> = ({
  children,
  className,
  maxCount,
  size,
  ...rest
}) => {
  const childArray = React.Children.toArray(children);
  const displayChildren = maxCount ? childArray.slice(0, maxCount) : childArray;
  const remainingCount = maxCount ? childArray.length - maxCount : 0;

  return (
    <div className={classNames('ecme-avatar-group', className)} {...rest}>
      {displayChildren.map((child, index) =>
        React.isValidElement(child)
          ? React.cloneElement(child as React.ReactElement<AvatarProps>, {
              size: size || (child.props as AvatarProps).size,
              key: index,
            })
          : child
      )}
      {remainingCount > 0 && (
        <Avatar size={size}>+{remainingCount}</Avatar>
      )}
    </div>
  );
};

export default Avatar;
